import {
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  type RefresherEventDetail,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { dayStart } from "@formkit/tempo";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { searchOutline } from "ionicons/icons";
import { SearchField } from "@/components/atoms/search-field";
import { Content } from "@/components/content";
import { OrganizationDropdown } from "@/components/molecules/organization/organization-dropdown";
import { ProjectCardCarousel } from "@/components/molecules/project/project-card.featured.carousel";
import { ProjectCardUpcoming } from "@/components/molecules/project/project-card.upcoming";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

const today = dayStart(new Date()).toISOString();
export function ProjectsPage() {
  const { t } = useTranslation();
  const ITEMS_PER_PAGE = 10;
  const [searchText, setSearchText] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const statusBarHeight = useStatusBarHeight();

  const { data: featuredProjectsData, refetch: refetchFeatured } = useQuery({
    queryKey: ["featured-projects", searchText, ITEMS_PER_PAGE],
    queryFn: async () => {
      const res = await api.project.getAll({
        filter: {
          endDate: {
            operator: "gt",
            value: today,
          },
          isFeatured: {
            operator: "equals",
            value: true,
          },
          ...(searchText
            ? {
                name: {
                  operator: "contains",
                  value: searchText,
                },
              }
            : undefined),
        },
        pagination: {
          take: ITEMS_PER_PAGE,
          page: 1,
        },
        sort: {
          field: "startDate",
          order: "asc",
        },
        aggregate: {
          uniqueUserFunctions: {
            compute: true,
          },
        },
      });
      return res.data;
    },
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchProjects,
  } = useInfiniteQuery({
    queryKey: ["projects", ITEMS_PER_PAGE, searchText],
    queryFn: async (args) => {
      const res = await api.project.getAll({
        filter: {
          endDate: {
            operator: "gte",
            value: today,
          },
          ...(searchText
            ? {
                name: {
                  operator: "contains",
                  value: searchText,
                },
              }
            : undefined),
        },
        aggregate: {
          uniqueUserFunctions: {
            compute: true,
          },
        },
        sort: {
          field: "startDate",
          order: "asc",
        },
        pagination: {
          take: ITEMS_PER_PAGE,
          page: args.pageParam + 1,
        },
      });
      return res.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalItems = lastPage.totalAmount;
      const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

      if (currentItems >= totalItems) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      return firstPageParam - 1;
    },
  });

  const upcomingProjects = data?.pages.flatMap((page) => page.items) ?? [];
  const featuredProjects = featuredProjectsData?.items ?? [];

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchFeatured(), refetchProjects()]);
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar
          className="ion-no-border plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]"
          style={{ paddingTop: `${statusBarHeight}px` }}
        >
          {isSearchExpanded ? (
            <SearchField
              value={searchText}
              onChange={setSearchText}
              onBlur={() => {
                if (!searchText) {
                  setIsSearchExpanded(false);
                }
              }}
              placeholder={t("common:search.placeholder")}
              autoFocus
              debounce={300}
            />
          ) : (
            <div className="flex items-center justify-between">
              <OrganizationDropdown />
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                className="rounded-lg p-2 transition-opacity active:opacity-70"
              >
                <IonIcon icon={searchOutline} className="text-lg text-txt-primary" />
              </button>
            </div>
          )}
        </Toolbar>
      </IonHeader>
      <Content>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>
        {featuredProjects.length > 0 && (
          <>
            <IonHeader collapse="condense" className="ion-no-border">
              <IonToolbar style={{ paddingTop: `${statusBarHeight}px` }}>
                <IonTitle className="p-0 text-start text-xl font-semibold ion-text-txt-primary">
                  {t("common:featured.title")}
                </IonTitle>
              </IonToolbar>
            </IonHeader>

            <IonList className="flex flex-row gap-4">
              <ProjectCardCarousel projects={featuredProjects} />
            </IonList>
          </>
        )}
        <IonToolbar>
          <IonTitle className="p-0 text-start text-xl font-semibold ion-text-txt-primary">
            {t("common:soon.title")}
          </IonTitle>
        </IonToolbar>
        <IonList className="flex flex-col gap-4">
          {upcomingProjects.map((project) => (
            <ProjectCardUpcoming key={project?.id} project={project} />
          ))}
        </IonList>
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage}>
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </Content>
    </IonPage>
  );
}
